/**
 * Help Context
 *
 * Purpose: Global help state provider with useHelp hook
 * Created: March 24, 2026
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

import { usePageHelp } from '@xanectech/data';
import { HelpSideSheet } from '../help/HelpSideSheet';
import type { HelpApp, HelpContextState } from '@xanectech/types';

const HelpContext = createContext<HelpContextState | null>(null);

interface HelpProviderProps {
  app: HelpApp;
  children: React.ReactNode;
}

export const HelpProvider: React.FC<HelpProviderProps> = ({
  app,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeArticleSlug, setActiveArticleSlug] = useState<
    string | undefined
  >(undefined);
  const [activeCategorySlug, setActiveCategorySlug] = useState<
    string | undefined
  >(undefined);
  const [activeHelpType, setActiveHelpType] = useState<'article' | 'category'>(
    'article',
  );

  const { data: pageMapping } = usePageHelp(app);

  const openPageHelp = useCallback(
    (slug?: string) => {
      if (slug) {
        // Explicit slug always opens as article
        setActiveArticleSlug(slug);
        setActiveCategorySlug(undefined);
        setActiveHelpType('article');
      } else if (pageMapping) {
        const helpType = (
          pageMapping.help_type === 'category' ? 'category' : 'article'
        ) as 'article' | 'category';
        setActiveHelpType(helpType);
        if (helpType === 'category') {
          setActiveCategorySlug(pageMapping.help_slug);
          setActiveArticleSlug(undefined);
        } else {
          setActiveArticleSlug(pageMapping.help_slug);
          setActiveCategorySlug(undefined);
        }
      } else {
        setActiveArticleSlug(undefined);
        setActiveCategorySlug(undefined);
        setActiveHelpType('article');
      }
      setIsOpen(true);
    },
    [pageMapping],
  );

  const closeHelp = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleNavigate = useCallback((slug: string) => {
    if (!slug) {
      // Going back to category view
      setActiveArticleSlug(undefined);
    } else {
      setActiveArticleSlug(slug);
    }
  }, []);

  const value: HelpContextState = {
    app,
    isOpen,
    activeArticleSlug,
    activeCategorySlug,
    activeHelpType,
    openPageHelp,
    closeHelp,
  };

  return (
    <HelpContext.Provider value={value}>
      {children}
      <HelpSideSheet
        open={isOpen}
        onOpenChange={setIsOpen}
        articleSlug={activeArticleSlug}
        categorySlug={activeCategorySlug}
        helpType={activeHelpType}
        onNavigate={handleNavigate}
        app={app}
      />
    </HelpContext.Provider>
  );
};

/**
 * Hook to access help context from any component inside a HelpProvider
 */
export const useHelp = (): HelpContextState => {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
};

/**
 * Safe version of useHelp that returns null when outside a HelpProvider.
 * Useful for shared components like PortalToolbar that may render
 * before a HelpProvider is available.
 */
export const useHelpSafe = (): HelpContextState | null => {
  return useContext(HelpContext);
};
